import { loader } from 'graphql.macro';

import { getCookie, setCookie } from '../cookies';
import { CartApolloClient, CartPSClient } from './client';

import {
  CreateCartMutation,
  CreateCartMutationVariables,
} from './cart-api-queries-mutations/CreateCartMutation.shopify.generated';
import { GetCartQuery, GetCartQueryVariables } from './cart-api-queries-mutations/GetCart.shopify.generated';
import {
  GetCartQuery as GetCartQueryProductService,
  GetCartQueryVariables as GetCartQueryVariablesProductService,
} from './product-service-queries-mutations/GetCart.product-service.generated';
import {
  AddCartLinesMutation,
  AddCartLinesMutationVariables,
} from './cart-api-queries-mutations/AddCartLine.shopify.generated';
import {
  AddCartLinesMutation as AddCartLinesMutationProductService,
  AddCartLinesMutationVariables as AddCartLinesMutationVariablesProductService,
} from './product-service-queries-mutations/AddCartLine.product-service.generated';
import {
  UpdateCartLinesMutation,
  UpdateCartLinesMutationVariables,
} from './cart-api-queries-mutations/UpdateCartLine.shopify.generated';
import {
  UpdateCartLinesMutation as UpdateCartLinesMutationProductService,
  UpdateCartLinesMutationVariables as UpdateCartLinesMutationVariablesProductService,
} from './product-service-queries-mutations/UpdateCartLine.product-service.generated';
import {
  CartNoteUpdateMutation,
  CartNoteUpdateMutationVariables,
} from './cart-api-queries-mutations/CartNoteUpdate.shopify.generated';
import {
  CartNoteUpdateMutation as CartNoteUpdateMutationProductService,
  CartNoteUpdateMutationVariables as CartNoteUpdateMutationVariablesProductService,
} from './product-service-queries-mutations/CartNoteUpdate.product-service.generated';
import {
  UpdateCartDiscountCodesMutation,
  UpdateCartDiscountCodesMutationVariables,
} from './cart-api-queries-mutations/UpdateCartDiscount.shopify.generated';
import {
  UpdateCartDiscountCodesMutation as UpdateCartDiscountCodesMutationProductService,
  UpdateCartDiscountCodesMutationVariables as UpdateCartDiscountCodesMutationVariablesProductService,
} from './product-service-queries-mutations/UpdateCartDiscount.product-service.generated';
import {
  CartBuyerIdentityUpdateMutation,
  CartBuyerIdentityUpdateMutationVariables,
  CountryCode,
} from 'src/generated/shopify-graphql';
import {
  CartAttributesUpdateMutation as CartAttributesUpdateMutationProductService,
  CartAttributesUpdateMutationVariables as CartAttributesUpdateMutationVariablesProductService,
} from './product-service-queries-mutations/CartAttributesUpdate.product-service.generated';
import {
  CartBuyerIdentityUpdateMutation as CartBuyerIdentityUpdateMutationProductService,
  CartBuyerIdentityUpdateMutationVariables as CartBuyerIdentityUpdateMutationVariablesProductService,
} from './product-service-queries-mutations/CartBuyerIdentityUpdate.product-service.generated';
import {
  MergeCartOnLoginMutation,
  MergeCartOnLoginMutationVariables,
} from './product-service-queries-mutations/MergeCartOnLogin.product-service.generated';
import {
  MigrateActiveCartMutation,
  MigrateActiveCartMutationVariables,
} from './product-service-queries-mutations/MigrateActiveCart.product-service.generated';

import { Customer } from '../environment';
import { generateAjaxCartCookie, generateStorefrontCookie, shouldUsePersistentCartMode } from './cart-api-utils';
import { getPScontext, recordCustomEventToNewRelic } from '../helpers';
import { removeGWPitemsFromCart } from '.';
import { prependLocaleToPath } from '../localization';
import { pushCartInfoToDataLayer } from '../gtm-data-layer';

const extendedCreateCartMutation = loader('./cart-api-queries-mutations/CreateCartMutation.shopify.graphql');
const extendedGetCartQuery = loader('./cart-api-queries-mutations/GetCart.shopify.graphql');
const extendedGetCartQueryProductService = loader(
  './product-service-queries-mutations/GetCart.product-service.graphql'
);
const extendedAddLineCartMutation = loader('./cart-api-queries-mutations/AddCartLine.shopify.graphql');
const extendedAddLineCartMutationProductService = loader(
  './product-service-queries-mutations/AddCartLine.product-service.graphql'
);
const extendedUpdateCartLineMutation = loader('./cart-api-queries-mutations/UpdateCartLine.shopify.graphql');
const extendedUpdateCartLineMutationProductService = loader(
  './product-service-queries-mutations/UpdateCartLine.product-service.graphql'
);
const extendedUpdateCartDiscountMutation = loader('./cart-api-queries-mutations/UpdateCartDiscount.shopify.graphql');
const extendedUpdateCartDiscountMutationProductService = loader(
  './product-service-queries-mutations/UpdateCartDiscount.product-service.graphql'
);
const extendedCartNoteMutation = loader('./cart-api-queries-mutations/CartNoteUpdate.shopify.graphql');
const extendedCartNoteMutationProductService = loader(
  './product-service-queries-mutations/CartNoteUpdate.product-service.graphql'
);
const cartBuyerIdentityMutation = loader('./cart-api-queries-mutations/CartBuyerIdentityUpdate.shopify.graphql');
const cartBuyerIdentityMutationProductService = loader(
  './product-service-queries-mutations/CartBuyerIdentityUpdate.product-service.graphql'
);
const psMergeCartOnLoginMutation = loader(
  './product-service-queries-mutations/MergeCartOnLogin.product-service.graphql'
);
const psMigrateActiveCartMutation = loader(
  './product-service-queries-mutations/MigrateActiveCart.product-service.graphql'
);
const psCartAttributesUpdateMutation = loader(
  './product-service-queries-mutations/CartAttributesUpdate.product-service.graphql'
);

const SHOPIFY_CART_EXPIRE_DAYS = 30;

// This mutation creates a cart and returns information about the cart.
export const createCart = async () => {
  try {
    const variables = { cartInput: {} };
    const email = Customer?.email;

    variables.cartInput = {
      buyerIdentity: {
        countryCode: window?.Shopify?.country ?? 'US',
        email,
      },
    };
    const createCartResponse = await CartApolloClient.mutate<CreateCartMutation, CreateCartMutationVariables>({
      mutation: extendedCreateCartMutation,
      variables: variables,
    });
    const { id } = createCartResponse.data?.cartCreate?.cart || { id: '' };
    const cartCookie = generateAjaxCartCookie(id);
    setCookie('cart', cartCookie, SHOPIFY_CART_EXPIRE_DAYS);
    if (!createCartResponse.data?.cartCreate?.cart && createCartResponse.data?.cartCreate?.userErrors?.[0]?.message) {
      throw new Error(JSON.stringify(createCartResponse.data?.cartCreate?.userErrors));
    }
    if (!createCartResponse || createCartResponse.errors) {
      throw new Error(JSON.stringify(createCartResponse?.errors) || 'createCartResponse is undefined');
    }
    return createCartResponse.data?.cartCreate;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    recordCustomEventToNewRelic('create_cart_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'createCart failed: ' + message,
    });
    console.log('Cart creation failed.');
  }
  return null;
};

// Query a cart by id and return some of the cart's objects.
export const fetchCart = async () => {
  const cartCookie = getCookie('cart');
  const cartId = generateStorefrontCookie(cartCookie);
  try {
    if (cartId) {
      const variables = {
        cartId,
      };
      const cartResponse = await CartApolloClient.query<GetCartQuery, GetCartQueryVariables>({
        query: extendedGetCartQuery,
        variables: variables,
      });
      const data = cartResponse.data;

      // update buyer identity in-case if there is any mismatch, especially when
      // the user change country by url or when the customer logged in/out.
      const email = Customer?.email;
      const countryCode = window?.Shopify?.country ?? 'US';
      const isChangeBuyerIdentity =
        data.cart?.buyerIdentity?.countryCode !== countryCode || data.cart?.buyerIdentity?.email !== email;
      if (!data) {
        setCookie('cart', cartId, 0);
        const createCartResponse = await createCart();
        return createCartResponse;
      } else if (isChangeBuyerIdentity) {
        const options = {
          countryCode,
          email,
        };
        const buyerIdentityUpdateResponse = await updateCartBuyerIdentity(options);
        return buyerIdentityUpdateResponse;
      }
      return data;
    } else {
      const createCartResponse = await createCart();
      return createCartResponse;
    }
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('fetch_cart_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      message: 'fetchCart failed: ' + message,
      customerID: Customer?.id || '',
      cartIdFromCookie,
      cartId,
    });
    console.log('Cart fetching failed.');
    return null;
  }
};

// Get cart details from Product Service.
export const fetchCartFromProductService = async () => {
  const isDeviceIdSet = getCookie('isDeviceIdSet');
  try {
    let response;
    if (isDeviceIdSet) {
      const cartResponse = await CartPSClient.query<GetCartQueryProductService, GetCartQueryVariablesProductService>({
        query: extendedGetCartQueryProductService,
        context: getPScontext(),
      });
      response = cartResponse.data.getCart;
    } else {
      const migrateActiveCartResponse = await migrateActiveCart();
      response = migrateActiveCartResponse;
    }

    const { id } = response?.cart || { id: '' };
    const cartCookie = generateAjaxCartCookie(id);
    if (cartCookie) {
      setCookie('cart', cartCookie, SHOPIFY_CART_EXPIRE_DAYS);
    }

    // update buyer identity in-case if there is any mismatch, especially when
    // the user change country by url or when the customer logged in/out.
    const email = Customer?.email;
    const countryCode = window?.Shopify?.country ?? 'US';
    const isChangeBuyerIdentity =
      response?.cart?.buyerIdentity?.countryCode !== countryCode || response?.cart?.buyerIdentity?.email !== email;

    if (isChangeBuyerIdentity) {
      await removeGWPitemsFromCart(countryCode); // Remove ineligible GWP items from the cart on
      const options = {
        countryCode,
        email,
      };
      const buyerIdentityUpdateResponse = await updateCartBuyerIdentity(options);
      return buyerIdentityUpdateResponse;
    }

    pushCartInfoToDataLayer({
      customer_id: Customer.id ?? '',
      is_multi_cart_user: response?.customMetaField?.isMultiCartUser || false,
    });

    const existingAttributes = response?.cart.attributes || [];

    const isMultiCartUserAttr = existingAttributes.find((attr) => attr.key === 'is_multi_cart_user');
    const currentIsMultiCartUser = (response?.customMetaField?.isMultiCartUser || false).toString();

    const shouldUpdate = !isMultiCartUserAttr || isMultiCartUserAttr.value !== currentIsMultiCartUser;

    if (shouldUpdate) {
      try {
        const validCartAttributes = existingAttributes
          .filter((attr) => attr.value != null && attr.key !== 'is_multi_cart_user')
          .map((attr) => ({ key: attr.key, value: String(attr.value) }));

        validCartAttributes.push({ key: 'is_multi_cart_user', value: currentIsMultiCartUser });

        await cartAttributeUpdate(validCartAttributes);
      } catch (error) {
        let message = error;
        if (typeof error === 'object') {
          message = JSON.stringify(error);
        }
        const cartIdFromCookie = getCookie('cart');
        const cartId = generateStorefrontCookie(cartIdFromCookie);
        recordCustomEventToNewRelic('cart_attributes_update_exception', {
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          customerID: Customer?.id || '',
          message: 'cartAttributesUpdate failed: ' + message,
          cartId,
        });
        console.error('Failed to update cart attribute:', error);
      }
    }

    if (cartCookie) {
      window.fetchCartFinished = true;
    }
    return response;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    recordCustomEventToNewRelic('fetch_cart_from_ps_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'fetchCartFromProductService failed: ' + message,
    });
    console.log('Cart fetching failed.');
    return null;
  }
};

export const cartAttributeUpdate = async (cart_attributes: { key: string; value: string }[]) => {
  try {
    await CartPSClient.mutate<
      CartAttributesUpdateMutationProductService,
      CartAttributesUpdateMutationVariablesProductService
    >({
      mutation: psCartAttributesUpdateMutation,
      variables: {
        attributes: cart_attributes,
      },
      context: getPScontext(),
    });
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('cart_attributes_update_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'cartAttributesUpdate failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.error('cartAttributeUpdate failed:', error);
  }
};

const updateCartCookieIfChanged = (returnedCartId: string, currentCartId: string) => {
  if (returnedCartId && returnedCartId !== currentCartId) {
    const cartCookie = generateAjaxCartCookie(returnedCartId);
    if (cartCookie) {
      setCookie('cart', cartCookie, SHOPIFY_CART_EXPIRE_DAYS);
    }
  }
};

// This mutation adds lines to existing cart, returns the quantity and product id.
export const addLineItemToCart = async (items: LineItems) => {
  const IS_PERSISTENT_CART_ENABLED = await shouldUsePersistentCartMode();
  const cartCookie = getCookie('cart');
  const cartId = generateStorefrontCookie(cartCookie);

  const lines = items.map((item) => ({
    merchandiseId: `gid://shopify/ProductVariant/${item.merchandiseId}`,
    quantity: item.quantity,
    attributes: item.cartItemAttributes ?? [],
  }));
  try {
    const variables = {
      cartId,
      lines,
    };
    let cartResponse;
    if (IS_PERSISTENT_CART_ENABLED) {
      cartResponse = await CartPSClient.mutate<
        AddCartLinesMutationProductService,
        AddCartLinesMutationVariablesProductService
      >({
        mutation: extendedAddLineCartMutationProductService,
        variables: variables,
        context: getPScontext(),
      });
    } else {
      cartResponse = await CartApolloClient.mutate<AddCartLinesMutation, AddCartLinesMutationVariables>({
        mutation: extendedAddLineCartMutation,
        variables: variables,
      });
    }
    if (!cartResponse.data?.cartLinesAdd?.cart && cartResponse.data?.cartLinesAdd?.userErrors?.[0]?.message) {
      throw new Error(JSON.stringify(cartResponse.data?.cartLinesAdd?.userErrors));
    }
    if (!cartResponse || cartResponse.errors) {
      throw new Error(JSON.stringify(cartResponse?.errors) || 'cartResponse is undefined');
    }
    const returnedCartId = cartResponse.data?.cartLinesAdd?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', cartId);
    return cartResponse.data?.cartLinesAdd;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('add_line_item_to_cart_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'addLineItemToCart failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.log('Add line item to cart failed.');
  }
  return null;
};

// This mutation is used to add a product variant of the same type to the cart. In the below example, the quantity
// of the variant (in variables.json) is increased and the id and quantity are returned to confirm they are correct.
export const updateCartLineItem = async (lineIds: string[], quantity: number) => {
  try {
    const IS_PERSISTENT_CART_ENABLED = await shouldUsePersistentCartMode();
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const lines = lineIds?.map((id) => ({
      id: id || '',
      quantity,
    }));
    const variables = {
      cartId,
      lines,
    };
    let cartResponse;
    if (IS_PERSISTENT_CART_ENABLED) {
      cartResponse = await CartPSClient.mutate<
        UpdateCartLinesMutationProductService,
        UpdateCartLinesMutationVariablesProductService
      >({
        mutation: extendedUpdateCartLineMutationProductService,
        variables: variables,
        context: getPScontext(),
      });
    } else {
      cartResponse = await CartApolloClient.mutate<UpdateCartLinesMutation, UpdateCartLinesMutationVariables>({
        mutation: extendedUpdateCartLineMutation,
        variables: variables,
      });
    }
    if (!cartResponse.data?.cartLinesUpdate?.cart && cartResponse.data?.cartLinesUpdate?.userErrors?.[0]?.message) {
      throw new Error(JSON.stringify(cartResponse.data?.cartLinesUpdate?.userErrors));
    }
    if (!cartResponse || cartResponse.errors) {
      throw new Error(JSON.stringify(cartResponse?.errors) || 'cartResponse is undefined');
    }
    const returnedCartId = cartResponse.data?.cartLinesUpdate?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', cartId);
    return cartResponse.data?.cartLinesUpdate;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('update_cart_line_item_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'updateCartLineItem failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.log('Update line item to cart API failed.', error);
  }
  return null;
};

// This mutation updates the discount codes applied to a given cart and returns the cart id and
// discountCodes' 'code' and 'applicable' fields
export const applyPromoCodeToCart = async (coupon: string) => {
  try {
    const IS_PERSISTENT_CART_ENABLED = await shouldUsePersistentCartMode();
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      cartId,
      discountCodes: [coupon],
    };
    let cartResponse;
    if (IS_PERSISTENT_CART_ENABLED) {
      cartResponse = await CartPSClient.mutate<
        UpdateCartDiscountCodesMutationProductService,
        UpdateCartDiscountCodesMutationVariablesProductService
      >({
        mutation: extendedUpdateCartDiscountMutationProductService,
        variables: variables,
        context: getPScontext(),
      });
    } else {
      cartResponse = await CartApolloClient.mutate<
        UpdateCartDiscountCodesMutation,
        UpdateCartDiscountCodesMutationVariables
      >({
        mutation: extendedUpdateCartDiscountMutation,
        variables: variables,
      });
    }

    if (
      !cartResponse.data?.cartDiscountCodesUpdate?.cart &&
      cartResponse.data?.cartDiscountCodesUpdate?.userErrors?.[0]?.message
    ) {
      throw new Error(JSON.stringify(cartResponse.data?.cartDiscountCodesUpdate?.userErrors));
    }
    if (!cartResponse || cartResponse.errors) {
      throw new Error(JSON.stringify(cartResponse?.errors) || 'cartResponse is undefined');
    }
    const returnedCartId = cartResponse.data?.cartDiscountCodesUpdate?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', cartId);
    return cartResponse.data?.cartDiscountCodesUpdate;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('apply_promo_code_to_cart_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'applyPromoCodeToCart failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.log('Apply promo code failed.');
  }
};

// Updates cart note, returns cart.
export const cartNoteUpdate = async (note: string) => {
  try {
    const IS_PERSISTENT_CART_ENABLED = await shouldUsePersistentCartMode();
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      cartId,
      note,
    };

    let cartResponse;
    if (IS_PERSISTENT_CART_ENABLED) {
      cartResponse = await CartPSClient.mutate<
        CartNoteUpdateMutationProductService,
        CartNoteUpdateMutationVariablesProductService
      >({
        mutation: extendedCartNoteMutationProductService,
        variables: variables,
        context: getPScontext(),
      });
    } else {
      cartResponse = await CartApolloClient.mutate<CartNoteUpdateMutation, CartNoteUpdateMutationVariables>({
        mutation: extendedCartNoteMutation,
        variables: variables,
      });
    }
    if (!cartResponse.data?.cartNoteUpdate?.cart && cartResponse.data?.cartNoteUpdate?.userErrors?.[0]?.message) {
      throw new Error(JSON.stringify(cartResponse.data?.cartNoteUpdate?.userErrors));
    }
    if (!cartResponse || cartResponse.errors) {
      throw new Error(JSON.stringify(cartResponse?.errors) || 'cartResponse is undefined');
    }
    const returnedCartId = cartResponse.data?.cartNoteUpdate?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', cartId);
    return cartResponse.data?.cartNoteUpdate;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('cart_note_update_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'cartNoteUpdate failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.log('Cart note update failed.');
  }
};

// Updates buyer identity and return cart.
// In-order to remove the buyer identity from cart we need to pass email and customerAccessToken as null
export const updateCartBuyerIdentity = async (options: buyerIdentityOptions) => {
  try {
    const IS_PERSISTENT_CART_ENABLED = await shouldUsePersistentCartMode();
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      buyerIdentity: {
        email: options.email,
        countryCode: options.countryCode as CountryCode,
      },
      cartId,
    };
    let cartResponse;
    if (IS_PERSISTENT_CART_ENABLED) {
      cartResponse = await CartPSClient.mutate<
        CartBuyerIdentityUpdateMutationProductService,
        CartBuyerIdentityUpdateMutationVariablesProductService
      >({
        mutation: cartBuyerIdentityMutationProductService,
        variables: variables,
        context: getPScontext(),
      });
    } else {
      cartResponse = await CartApolloClient.mutate<
        CartBuyerIdentityUpdateMutation,
        CartBuyerIdentityUpdateMutationVariables
      >({
        mutation: cartBuyerIdentityMutation,
        variables: variables,
      });
    }
    if (
      !cartResponse.data?.cartBuyerIdentityUpdate?.cart &&
      cartResponse.data?.cartBuyerIdentityUpdate?.userErrors?.[0]?.message
    ) {
      throw new Error(JSON.stringify(cartResponse.data?.cartBuyerIdentityUpdate?.userErrors));
    }
    if (!cartResponse || cartResponse.errors) {
      throw new Error(JSON.stringify(cartResponse?.errors) || 'cartResponse is undefined');
    }
    const returnedCartId = cartResponse.data?.cartBuyerIdentityUpdate?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', cartId);
    return cartResponse.data?.cartBuyerIdentityUpdate;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('update_cart_buyer_identity_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'updateCartBuyerIdentity failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    console.log('cart Buyer Identity Update failed.');
  }
};

export const mergeCartOnLogin = async (email: string, PScontext = getPScontext()) => {
  try {
    if (
      !PScontext.headers['X-Shopify-Customer-ID'] ||
      !PScontext.headers['X-Shopify-Timestamp'] ||
      !PScontext.headers['X-Shopify-Signature']
    ) {
      throw new Error('Customer credentials cannot be empty');
    }
    const buyerIdentity = {
      countryCode: window?.Shopify?.country ?? 'US',
      email,
    };
    const mergeResponse = await CartPSClient.mutate<MergeCartOnLoginMutation, MergeCartOnLoginMutationVariables>({
      mutation: psMergeCartOnLoginMutation,
      variables: {
        buyerIdentity,
      },
      context: PScontext,
    });
    const { id } = mergeResponse.data?.mergeCartOnLogin?.cart || { id: '' };
    if (!mergeResponse.data?.mergeCartOnLogin?.cart && mergeResponse.data?.mergeCartOnLogin?.userErrors?.[0]?.message) {
      throw new Error(JSON.stringify(mergeResponse.data?.mergeCartOnLogin?.userErrors));
    }
    if (!mergeResponse || mergeResponse.errors) {
      throw new Error(JSON.stringify(mergeResponse?.errors) || 'mergeResponse is undefined');
    }
    const returnedCartId = mergeResponse.data?.mergeCartOnLogin?.cart?.id;
    updateCartCookieIfChanged(returnedCartId || '', id);
    return id;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    recordCustomEventToNewRelic('merge_cart_on_login_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'mergeCartOnLogin failed: ' + message,
    });
    console.log('Cart merge failed on login.', error);
  }
};

// This mutation migrates active cart to Product Service.
export const migrateActiveCart = async () => {
  try {
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    const psMigrateActiveCartResponse = await CartPSClient.mutate<
      MigrateActiveCartMutation,
      MigrateActiveCartMutationVariables
    >({
      mutation: psMigrateActiveCartMutation,
      variables: {
        cartId,
      },
      context: getPScontext(),
    });

    const { id } = psMigrateActiveCartResponse.data?.migrateActiveCart?.cart || { id: '' };
    const cartCookie = generateAjaxCartCookie(id);
    if (
      !psMigrateActiveCartResponse.data?.migrateActiveCart?.cart &&
      psMigrateActiveCartResponse.data?.migrateActiveCart?.userErrors?.[0]?.message
    ) {
      throw new Error(JSON.stringify(psMigrateActiveCartResponse.data?.migrateActiveCart?.userErrors));
    }
    if (!psMigrateActiveCartResponse || psMigrateActiveCartResponse.errors) {
      throw new Error(JSON.stringify(psMigrateActiveCartResponse?.errors) || 'cartResponse is undefined');
    }
    if (cartCookie) {
      setCookie('cart', cartCookie, SHOPIFY_CART_EXPIRE_DAYS);
      setCookie('isDeviceIdSet', 'true', SHOPIFY_CART_EXPIRE_DAYS);
    } else {
      throw new Error('Failed to set cart cookie');
    }
    return psMigrateActiveCartResponse.data?.migrateActiveCart;
  } catch (error) {
    let message = error;
    if (typeof error === 'object') {
      message = JSON.stringify(error);
    }
    const cartIdFromCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartIdFromCookie);
    recordCustomEventToNewRelic('migrate_active_cart_error', {
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      customerID: Customer?.id || '',
      message: 'migrateActiveCart failed: ' + message,
      cartIdFromCookie,
      cartId,
    });
    throw new Error('Migrate active cart failed.', { cause: error });
  }
};

export const triggerMergeCartOnLogin = async (setCustomHeader: boolean = false) => {
  if (await shouldUsePersistentCartMode()) {
    try {
      let newCartID;
      if (!setCustomHeader) {
        newCartID = await mergeCartOnLogin(String(Customer?.email || ''));
      } else {
        const logged_in_user_details = await fetch(prependLocaleToPath('/pages/get_logged_in_user_details'), {
          method: 'get',
        }).then((res) => res.text());
        const userObject = JSON.parse(logged_in_user_details);
        const basePScontext = getPScontext();

        const header = {
          ...basePScontext,
          headers: {
            ...basePScontext.headers,
            'X-Shopify-Customer-ID': userObject?.customer_id?.toString() || '',
            'X-Shopify-Timestamp': userObject?.timestamp || '',
            'X-Shopify-Signature': userObject?.signature || '',
          },
        };
        newCartID = await mergeCartOnLogin(String(userObject?.email || ''), header);
      }

      const cartCookieValue = newCartID ? generateAjaxCartCookie(newCartID) : null;

      if (cartCookieValue) {
        setCookie('cart', cartCookieValue, 30);
      }
    } catch (err) {
      recordCustomEventToNewRelic('trigger_merge_cart_on_login_error', {
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        customerID: Customer?.id || '',
        message: 'triggerMergeCartOnLogin call failed: ' + err,
      });
      console.log('triggerMergeCartOnLogin call failed.', err);
    }
  }
};

export type LineItem = {
  merchandiseId: number;
  quantity: number;
  cartItemAttributes?: CartItemAttributes[];
};
export type LineItems = LineItem[];

export type VariantIds = Record<string, number>;

export type CartItemProperties = {
  [key: string]: string | number | boolean;
};

export type CartItemAttributes = {
  key: string;
  value: string;
};

export type Credentials = {
  email: string;
  password: string;
};

export interface buyerIdentityOptions {
  countryCode: string;
  customerAccessToken?: string | null;
  email?: string | null;
  phone?: string | null;
}
